import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { AppShell, Header, RequestIndicator, ThemeToggle, ToastViewport, useAuth } from 'grabbitbot-legacy-ui'
import { apiClient } from '../api/client'
import { ADMIN_ROLE } from '../config'

// Legacy `HeaderComponent` hardcoded its nav links to the `binance`/`spot`
// exchange+tradingType pair (`header.component.html`: `{{ urlPrefix }}` was
// always resolved to that pair in this app) — kept as the nav's default
// scope for round-1 parity. Any other exchange/tradingType pair is still
// reachable by direct URL (audit §3: these routes never had scope pickers
// either).
const DEFAULT_EXCHANGE = 'binance'
const DEFAULT_TRADING_TYPE = 'spot'

function NavLink({ to, children }: { to: string; children: ReactNode }) {
  const location = useLocation()
  const active = location.pathname === to || location.pathname.startsWith(`${to}/`)
  return (
    <Link to={to} aria-current={active ? 'page' : undefined} className={active ? 'active' : undefined}>
      {children}
    </Link>
  )
}

export function Shell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth()
  const isAdmin = Boolean(user?.roles?.includes(ADMIN_ROLE))

  return (
    <AppShell
      header={
        user ? (
          <Header
            title={<Link to="/coins">Crypto Pair Screener</Link>}
            nav={
              <div className="screener-nav">
                <NavLink to="/coins">Coins</NavLink>
                <NavLink to={`/${DEFAULT_EXCHANGE}/${DEFAULT_TRADING_TYPE}/momentums`}>Momentums</NavLink>
                <NavLink to={`/${DEFAULT_EXCHANGE}/${DEFAULT_TRADING_TYPE}/coinpairlists`}>Coinpair lists</NavLink>
                <NavLink to={`/${DEFAULT_EXCHANGE}/${DEFAULT_TRADING_TYPE}/indicatorconditions`}>
                  Indicator conditions
                </NavLink>
                <NavLink to="/textdata">TextData</NavLink>
                <NavLink to="/service-accounts">Service accounts</NavLink>
                <NavLink to="/payment">Payment</NavLink>
                {isAdmin && <NavLink to="/admin/users">Admin</NavLink>}
              </div>
            }
            actions={
              <div className="screener-actions">
                <RequestIndicator client={apiClient} />
                <ThemeToggle />
                <NavLink to="/user">{user.username}</NavLink>
                <button type="button" onClick={logout}>
                  Log out
                </button>
              </div>
            }
          />
        ) : undefined
      }
    >
      {children}
      <ToastViewport />
    </AppShell>
  )
}

export { DEFAULT_EXCHANGE, DEFAULT_TRADING_TYPE }
